import { birthDateSchema } from '@1trading/fast-api-domain/forms';
import { z } from 'zod';

import i18n from '@/lib/i18n';
import { mainstreamCountrySchema } from '@/services/api/mainstream/country';
import { AML_OTHER_ANSWER, amlAnswersSchema } from '@/services/api/profile';
import type { PartialExcept } from '@/types/generics';

export type AmlQuestionnaire = z.infer<typeof amlAnswersSchema>;

export type AmlQuestionnaireDefaults = PartialExcept<AmlQuestionnaire, 'occupation' | 'purposeOther' | 'fundsOriginOther'>;

export const amlQuestionnairePepSchema = amlAnswersSchema.pick({
  pep: true,
});

export type AmlQuestionnairePep = z.infer<typeof amlQuestionnairePepSchema>;

export const amlQuestionnaireEmploymentStatusSchema = amlAnswersSchema.pick({
  employmentStatus: true,
});

export type AmlQuestionnaireEmploymentStatus = z.infer<typeof amlQuestionnaireEmploymentStatusSchema>;

export const amlQuestionnaireEmploymentDetailsSchema = amlAnswersSchema.pick({
  occupation: true,
  annualNetIncome: true,
});

export type AmlQuestionnaireEmploymentDetails = z.infer<typeof amlQuestionnaireEmploymentDetailsSchema>;

export const amlQuestionnaireInvestmentSchema = amlAnswersSchema.pick({
  investmentPerYear: true,
});

export type AmlQuestionnaireInvestment = z.infer<typeof amlQuestionnaireInvestmentSchema>;

export const amlQuestionnaireWithdrawalSchema = amlAnswersSchema.pick({
  withdrawPerMonth: true,
});

export type AmlQuestionnaireWithdrawal = z.infer<typeof amlQuestionnaireWithdrawalSchema>;

export const amlQuestionnaireFiatDepositSchema = amlAnswersSchema.pick({
  depositFiatPerMonth: true,
});

export type AmlQuestionnaireFiatDeposit = z.infer<typeof amlQuestionnaireFiatDepositSchema>;

export const amlQuestionnaireCryptoDepositSchema = amlAnswersSchema.pick({
  depositCryptoPerMonth: true,
});

export type AmlQuestionnaireCryptoDeposit = z.infer<typeof amlQuestionnaireCryptoDepositSchema>;

export const amlQuestionnaireTradingVolumeSchema = amlAnswersSchema.pick({
  tradingVolume: true,
});

export type AmlQuestionnaireTradingVolume = z.infer<typeof amlQuestionnaireTradingVolumeSchema>;

export const amlQuestionnairePurposeAndFundsSchema = amlAnswersSchema
  .pick({
    purpose: true,
    purposeOther: true,
    fundsOrigin: true,
    fundsOriginOther: true,
  })
  .refine((data) => data.purpose !== AML_OTHER_ANSWER || !!data.purposeOther, {
    message: i18n.t('requiredField.error'),
    path: ['purposeOther'],
  })
  .refine((data) => data.fundsOrigin !== AML_OTHER_ANSWER || !!data.fundsOriginOther, {
    message: i18n.t('requiredField.error'),
    path: ['fundsOriginOther'],
  });

export type AmlQuestionnairePurposeAndFunds = z.infer<typeof amlQuestionnairePurposeAndFundsSchema>;

export const AML_QUESTIONNAIRE_DEFAULTS = {
  pep: undefined,
  employmentStatus: undefined,
  occupation: '',
  annualNetIncome: undefined,
  investmentPerYear: undefined,
  withdrawPerMonth: undefined,
  depositFiatPerMonth: undefined,
  depositCryptoPerMonth: undefined,
  tradingVolume: undefined,
  purpose: undefined,
  purposeOther: '',
  fundsOrigin: undefined,
  fundsOriginOther: '',
} satisfies AmlQuestionnaireDefaults;

export const amlQuestionnaireWithIdentitySchema = amlAnswersSchema.extend({
  birthCountry: z.string().min(1),
  birthDate: birthDateSchema({ errorPrefix: 'BIRTH_DATE' }),
  residencyCountry: mainstreamCountrySchema(),
});

export type AmlQuestionnaireWithIdentity = z.infer<typeof amlQuestionnaireWithIdentitySchema>;
